<template>
  <v-col align="center">
    <v-card width="1000">
      <v-card-title>
        <ProductCardHeader :product-id="product.id" title="User Selection" />
      </v-card-title>
      <v-card-text v-if="isLoading" style="color: #4F4F4F">
        <div class="text-center">
          <v-progress-circular indeterminate size="70" />
        </div>
      </v-card-text>
      <v-card-text v-else style="color: #4F4F4F">
        <v-row class="pa-3">
          Please select users who should have access to {{ product.name }}.
        </v-row>
        <v-row class="pa-3">
          To cancel subscription, remove all users.
        </v-row>
        <v-row>
          <v-col cols="10">
            <v-select v-model="userToBeAdded" :items="availableUsers" return-object item-text="initials">
              <template v-slot:selection="availableUsers">
                {{ availableUsers.item.name }} - ({{ availableUsers.item.initials }})
              </template>
              <template v-slot:item="availableUsers">
                {{ availableUsers.item.name }} - ({{ availableUsers.item.initials }})
              </template>
            </v-select>
          </v-col>
          <v-col col="2">
            <v-btn block color="#FF9E15" :disabled="invalidAvailableUsers" @click="addUser()">
              Add User
            </v-btn>
          </v-col>
        </v-row>
        <v-simple-table v-if="addedUsers.length > 0">
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left">Name</th>
                <th class="text-left">Initials</th>
                <th class="text-left">
                  Email
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <v-icon small color="#004C97" dark v-on="on">info</v-icon>
                    </template>
                    <span>Enter the email address to be used for sending and receiving documents for {{ product.name }}</span>
                  </v-tooltip>
                </th>
                <th class="text-left">
                  Administrator
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <v-icon small color="#004C97" dark v-on="on">info</v-icon>
                    </template>
                    <span>Administrators can change the Team Settings for {{ product.name }}. Anyone sharing the same email as an administrator will also have access to Team Settings.</span>
                  </v-tooltip>
                </th>
                <th v-if="shouldDisplayValidatedColumn" class="text-left">Validated?</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <UserSelectionRow v-for="user in addedUsers"
                                :key="user.initials"
                                :user="user"
                                :should-display-validated-column="shouldDisplayValidatedColumn"
                                :email.sync="user.email"
                                :is-admin.sync="user.isAdmin"
                                :is-newly-selected.sync="user.isNewlySelected"
                                @editEmailEvent="setAllMatchingEmails(user.email, user.isAdmin)"
                                @removeUserEvent="removeUserConfirm(user)" />
            </tbody>
          </template>
        </v-simple-table>
      </v-card-text>
      <v-card-actions>
        <v-row>
          <v-col cols="2" class="text-left">
            <v-btn block @click="back()">Back</v-btn>
          </v-col>
          <v-spacer></v-spacer>
          <v-col cols="6"><span class="red--text text-center">{{ errorMessages }}</span></v-col>
          <v-col cols="3" class="text-right">
            <v-btn color="#FF9E15" :disabled="disableSubmit" @click="submitUsers()">Submit Changes</v-btn>
          </v-col>
        </v-row>
      </v-card-actions>
    </v-card>
    <v-dialog v-model="dataLossDialog" width="400">
      <v-card>
        <v-card-title>
          Warning: Potential Loss of Progress
        </v-card-title>
        <v-card-text>
          {{ dialogMessage }}
        </v-card-text>
        <v-card-actions>
          <div class="flex-grow-1"></div>
          <v-btn @click="dataLossDialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="confirm()">Continue</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="removeConfirmationDialog" width="400">
      <v-card>
        <v-card-title>
          Confirm User Removal
        </v-card-title>
        <v-card-text>
          {{ dialogMessage }}
        </v-card-text>
        <v-card-actions>
          <div class="flex-grow-1"></div>
          <v-btn @click="removeConfirmationDialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="removeUser()">Remove User</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-col>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import ProductCardHeader from '@/components/products/shared/ProductCardHeader';
import emailValidatorMixin from '@/mixins/emailValidatorMixin';
import productIdMappingMixin from '@/mixins/productIdMappingMixin';
import UserSelectionRow from './UserSelectionRow';

export default {
  name: 'UserSelection',
  components: {
    ProductCardHeader,
    UserSelectionRow
  },
  mixins: [productIdMappingMixin, emailValidatorMixin],
  data () {
    return {
      isLoading: true,
      availableUsers: [],
      addedUsers: [],
      userToBeAdded: {},
      dialogMessage: '',
      dataLossDialog: false,
      dirty: false,
      removeConfirmationDialog: false,
      removedUser: {}
    };
  },
  computed: {
    ...mapState(['agency', 'insureSignUsers']),
    ...mapGetters(['getAvailableInsureSignAgencyUsers', 'getProductByProductId']),
    product () {
      return this.getProductByProductId(this.getProductId('eSignature'));
    },
    hasExistingUsers () {
      return this.insureSignUsers.length > 0;
    },
    invalidAvailableUsers () {
      return this.availableUsers.length < 1;
    },
    shouldDisplayValidatedColumn () {
      return this.insureSignUsers.length > 0 && this.product.isSubscribed;
    },
    errorMessages () {
      if (this.addedUsers.length < 1 && this.insureSignUsers.length > 0) {
        return 'There are no users selected. ';
      }
      if (this.insureSignUsers.length !== 0 && this.numberOfAdmins < 1) {
        return 'At least one administrator is required.';
      }
      var idx = this.addedUsers.findIndex(user => user.email === '' || !this.isValidEmail(user.email));
      if (idx > -1) {
        return `An invalid email address was found for user: "${this.addedUsers[idx].name}".`;
      }
      return '';
    },
    numberOfAdmins () {
      return this.addedUsers.filter(user => user.isAdmin === true).length;
    },
    disableSubmit () {
      return (this.dirty && this.errorMessages.length > 0) || (!this.dirty && this.addedUsers.length === 0);
    }
  },
  async created () {
    this.isLoading = true;

    var agencyUsersResponse = await this.$api.getAgencyUsers();
    if (agencyUsersResponse.code !== 0) {
      this.setError({ errorMessage: `Error - Unable to get agency users. Message: ${agencyUsersResponse.message}` });
      this.$router.push({ path: '/error' });
      return;
    }
    this.setAgencyUsers(JSON.parse(agencyUsersResponse.data[0]));

    var insureSignUsersResponse = await this.$api.getInsureSignUsers();
    if (insureSignUsersResponse.code !== 0 && insureSignUsersResponse.code !== 2) {
      this.setError({ errorMessage: `Error - Unable to get Formstack users. Message: ${insureSignUsersResponse.message}` });
      this.$router.push({ path: '/error' });
      return;
    }

    if (insureSignUsersResponse.code === 2) {
      insureSignUsersResponse.data = [];
    }

    this.setInsureSignUsers(insureSignUsersResponse.data);

    this.addedUsers = this.insureSignUsers;

    this.availableUsers = this.getAvailableInsureSignAgencyUsers;
    this.userToBeAdded = this.availableUsers[0];

    this.isLoading = false;
  },
  methods: {
    ...mapActions(['setSelectedInsureSignUsers', 'setAgencyUsers', 'setInsureSignUsers', 'setError']),
    addUser () {
      this.dirty = true;
      this.addedUsers.push(this.userToBeAdded);
      var idx = this.availableUsers.findIndex(user => user.initials === this.userToBeAdded.initials);
      this.availableUsers.splice(idx, 1);
      if (this.availableUsers.length > 0) {
        this.userToBeAdded = this.availableUsers[0];
      }
    },
    removeUserConfirm (user) {
      this.removedUser = user;
      if (!this.removedUser.isValidated) {
        this.dialogMessage = 'Please confirm the removal of user ' + this.removedUser.name;
      } else if (this.removedUser.isValidated && this.isLastEmail(this.removedUser.email)) {
        this.dialogMessage = `The ${this.product.name} account for ${this.removedUser.email} will be deactivated.`;
      } else {
        this.dialogMessage = this.removedUser.name + ' will be removed, however that email is still being used by one or more users.';
      }
      this.removeConfirmationDialog = true;
    },
    removeUser () {
      var idx = this.addedUsers.findIndex(user => user.initials === this.removedUser.initials);
      this.availableUsers.push(this.addedUsers[idx]);
      this.availableUsers.sort((a, b) => {
        const aName = a.name.toLowerCase();
        const bName = b.name.toLowerCase();

        if (aName < bName) { return -1; }
        if (aName > bName) { return 1; }

        if (a.initials < b.initials) { return -1; }
        if (a.initials > b.initials) { return 1; }

        return 0;
      });
      this.addedUsers.splice(idx, 1);
      this.userToBeAdded = this.availableUsers[0];
      this.dirty = true;
      this.removeConfirmationDialog = false;
    },
    isLastEmail (email) {
      var count = this.addedUsers.filter((x) => x.email === email).length;
      if (count <= 1) {
        return true;
      }
      return false;
    },
    setAllMatchingEmails (email, isAdmin) {
      this.addedUsers.forEach(function (user) {
        if (user.email === email) {
          user.isAdmin = isAdmin;
        }
      });
    },
    back () {
      if (!this.dirty) {
        this.confirm();
      }
      this.dialogMessage = 'If you continue, all unsaved changes will be lost.';
      this.dataLossDialog = true;
    },
    confirm () {
      this.$router.push('/product/eSignature');
    },
    submitUsers () {
      if (this.errorMessages) {
        return;
      }

      this.setSelectedInsureSignUsers(this.addedUsers);
      this.$router.push('/product/eSignature/billingConfirmation');
    }
  }
};

</script>
